import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import socketService from '../../services/socket';
import { useAuth } from '../../contexts/AuthContext';
import './Chat.css';

function ChatBubble() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [conversation, setConversation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [chatEnabled, setChatEnabled] = useState(true);
    const [notificationSound, setNotificationSound] = useState(true);
    const [uploading, setUploading] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Scroll to bottom of messages
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Fetch chat settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/chat/settings');
                setChatEnabled(response.data.chat_enabled);
                setNotificationSound(response.data.notification_sound !== false);
            } catch (err) {
                console.error('Error fetching chat settings:', err);
            }
        };
        fetchSettings();
    }, []);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await api.get('/chat/unread-count');
            setUnreadCount(response.data.count);
        } catch (err) {
            console.error('Error fetching unread count:', err);
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();
        // socket 'new_message' อัปเดต unread แบบ real-time อยู่แล้ว — poll นี้เป็น fallback (เดิม 30 วิ → 2 นาที)
        const interval = setInterval(fetchUnreadCount, 120000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Socket connection
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token && user) {
            socketService.connect(token);

            socketService.on('new_message', handleNewMessage);
            socketService.on('user_typing', handleTyping);
            socketService.on('messages_read', handleMessagesRead);

            return () => {
                socketService.off('new_message', handleNewMessage);
                socketService.off('user_typing', handleTyping);
                socketService.off('messages_read', handleMessagesRead);
            };
        }
    }, [user]);

    const handleNewMessage = useCallback((message) => {
        setMessages(prev => [...prev, message]);
        if (!isOpen) {
            setUnreadCount(prev => prev + 1);
            // Play notification sound if enabled
            if (notificationSound) {
                playNotificationSound();
            }
        }
        setTimeout(scrollToBottom, 100);
    }, [isOpen, scrollToBottom, notificationSound]);

    const handleTyping = useCallback(({ userId, isTyping: typing }) => {
        if (userId !== user?.user_id) {
            setIsTyping(typing);
        }
    }, [user]);

    const handleMessagesRead = useCallback(() => {
        setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
    }, []);

    const playNotificationSound = () => {
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {});
        } catch (e) {}
    };

    // Open chat and load conversation
    const openChat = async () => {
        setIsOpen(true);
        setLoading(true);
        try {
            const convResponse = await api.get('/chat/conversation');
            setConversation(convResponse.data);

            if (convResponse.data?.id) {
                const msgResponse = await api.get(`/chat/messages/${convResponse.data.id}`);
                setMessages(msgResponse.data);
                socketService.joinConversation(convResponse.data.id);

                // Mark as read
                await api.post('/chat/messages/read', {
                    conversationId: convResponse.data.id
                });
                setUnreadCount(0);
            }
        } catch (err) {
            console.error('Error loading chat:', err);
        } finally {
            setLoading(false);
            setTimeout(scrollToBottom, 100);
        }
    };

    const closeChat = () => {
        if (conversation?.id) {
            socketService.leaveConversation(conversation.id);
        }
        setIsOpen(false);
    };

    // Send message
    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || sending) return;

        setSending(true);
        try {
            const response = await api.post('/chat/messages', {
                conversationId: conversation?.id,
                content: newMessage.trim(),
                messageType: 'text'
            });

            if (!conversation?.id) {
                setConversation(response.data.conversation);
                socketService.joinConversation(response.data.conversation.id);
            }

            setMessages(prev => [...prev, response.data.message]);
            setNewMessage('');
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            console.error('Error sending message:', err);
            alert('ไม่สามารถส่งข้อความได้');
        } finally {
            setSending(false);
        }
    };

    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('ไฟล์ต้องมีขนาดไม่เกิน 10MB');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        if (conversation?.id) {
            formData.append('conversationId', conversation.id);
        }

        try {
            const response = await api.post('/chat/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (!conversation?.id) {
                setConversation(response.data.conversation);
                socketService.joinConversation(response.data.conversation.id);
            }

            setMessages(prev => [...prev, response.data.message]);
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            console.error('Error uploading file:', err);
            alert('ไม่สามารถอัพโหลดไฟล์ได้');
        } finally {
            setUploading(false);
            fileInputRef.current.value = '';
        }
    };

    // Handle typing indicator
    const handleTypingInput = () => {
        if (conversation?.id) {
            socketService.sendTyping(conversation.id, true);

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            typingTimeoutRef.current = setTimeout(() => {
                socketService.sendTyping(conversation.id, false);
            }, 2000);
        }
    };

    // Format timestamp
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('th-TH', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Render message content
    const renderMessageContent = (msg) => {
        switch (msg.message_type) {
            case 'image':
                return (
                    <img
                        src={msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url}
                        alt="รูปภาพ"
                        className="chat-image"
                        onClick={() => window.open(msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url, '_blank')}
                    />
                );
            case 'file':
                return (
                    <a
                        href={msg.file_url?.startsWith('/uploads') ? `/api${msg.file_url}` : msg.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chat-file"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                        </svg>
                        <span>{msg.file_name || 'ดาวน์โหลดไฟล์'}</span>
                    </a>
                );
            default:
                // Check for URLs in text
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const parts = msg.content.split(urlRegex);
                return (
                    <p>
                        {parts.map((part, i) =>
                            urlRegex.test(part) ? (
                                <a key={i} href={part} target="_blank" rel="noopener noreferrer">
                                    {part}
                                </a>
                            ) : part
                        )}
                    </p>
                );
        }
    };

    if (!chatEnabled) {
        return null;
    }

    return (
        <>
            {/* Chat Bubble Button */}
            <button
                className={`chat-bubble-btn ${isOpen ? 'hidden' : ''}`}
                onClick={openChat}
                title="แชทกับเจ้าหน้าที่การเงิน"
            >
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                </svg>
                {unreadCount > 0 && (
                    <span className="chat-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="chat-header-info">
                            <div className="chat-avatar admin">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                </svg>
                            </div>
                            <div>
                                <h4>ฝ่ายการเงิน</h4>
                                <span className="chat-status">
                                    {isTyping ? 'กำลังพิมพ์...' : 'ออนไลน์'}
                                </span>
                            </div>
                        </div>
                        <button className="chat-close-btn" onClick={closeChat}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                    </div>

                    <div className="chat-messages">
                        {loading ? (
                            <div className="chat-loading">
                                <div className="spinner"></div>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="chat-empty">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                                </svg>
                                <p>เริ่มต้นสนทนากับเจ้าหน้าที่การเงิน</p>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, index) => (
                                    <div
                                        key={msg.id || index}
                                        className={`chat-message ${msg.sender_type === 'user' ? 'sent' : 'received'}`}
                                    >
                                        {msg.sender_type !== 'user' && (
                                            <div className="chat-avatar small">
                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                                </svg>
                                            </div>
                                        )}
                                        <div className="message-bubble">
                                            {renderMessageContent(msg)}
                                            <span className="message-time">
                                                {formatTime(msg.created_at)}
                                                {msg.sender_type === 'user' && (
                                                    <span className={`read-status ${msg.is_read ? 'read' : ''}`}>
                                                        {msg.is_read ? '✓✓' : '✓'}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {isTyping && (
                                    <div className="chat-message received">
                                        <div className="chat-avatar small">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                            </svg>
                                        </div>
                                        <div className="message-bubble typing">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    <form className="chat-input-area" onSubmit={sendMessage}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="chat-attach-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <div className="spinner small"></div>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                                </svg>
                            )}
                        </button>
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyUp={handleTypingInput}
                            placeholder="พิมพ์ข้อความ..."
                            disabled={sending || uploading}
                        />
                        <button
                            type="submit"
                            className="chat-send-btn"
                            disabled={!newMessage.trim() || sending}
                        >
                            {sending ? (
                                <div className="spinner small"></div>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            )}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

export default ChatBubble;
